import '../../style/partner/invoices.css'

import { isEqual, pick } from 'lodash'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Prompt } from 'react-router-dom'

import { createCentralBackendPartner } from '../../redux/thunks/adminThunks'
import { processApiError } from '../../utils/root'
import BeamButton from '../root/BeamButton'
import { BeamModal } from '../root/BeamModal'
import { BeamProgressBar } from '../root/BeamProgressBar'
import { PageNotFound } from '../root/PageNotFound'
import { EditCBPartnerForm, formatData } from './EditCBPartnerForm'
import EditPortalPartnerForm from './EditPortalPartnerForm'

export type NonprofitOption = {
  value: number
  display: string
}

export type AggregateTargetChainNonprofitInfo = {
  chainNonprofitId?: string | number
  active: boolean
  causeDisplayIcon?: any
  causeDisplayName?: string
  causeSelectedImage?: any
  chainImpactImage?: any
  chainImpactDescription?: string
  filters?: string | string[]
  name: string
  nonprofitId: number
  sortOrder?: number
  targetChainDonationAmount?: number
  targetUserDonationAmount?: number
  userImpactDescription?: string
  includeInPersonalImpact?: boolean
}

export type StoreNonProfitOptions = {
  id: number
  name: string
}

export type StoreInfo = {
  name?: string
  displayName?: string
  communityPageURL?: string
  zipcodes?: string
  nonprofitIds: Array<NonprofitOption>
  type?: number
  id?: number
  active?: boolean
  regionZipCodes?: string[]
  nonprofits?: StoreNonProfitOptions[]
}

export type CBPartnerInfo = {
  createStagingChain?: boolean
  id?: string
  name: string
  logoImage?: any
  communityImpactLink?: string
  isPercentBased?: boolean
  isInKind?: boolean
  donationPercentage?: number
  donationAmount?: number
  useRegionalization: boolean
  targetChainNonprofitInfo: Array<AggregateTargetChainNonprofitInfo>
  stores: Array<StoreInfo>
}

export type PortalPartner = {
  launchDate?: string
  invoiceFirstName: string
  invoiceLastName: string
  invoiceEmail: string
  paymentStructureId?: number
  invoiceMinimum: number
}

const CreateCentralBackendPartner = () => {
  const defaultStore: StoreInfo = {
    name: 'Default Store',
    type: 3,
    zipcodes: '',
    nonprofitIds: [],
  }

  const defaultCBPartner: CBPartnerInfo = {
    createStagingChain: false,
    name: '',
    logoImage: undefined,
    communityImpactLink: '',
    isPercentBased: true,
    isInKind: false,
    donationPercentage: 0.01,
    donationAmount: 1,
    useRegionalization: false,
    targetChainNonprofitInfo: [],
    stores: [defaultStore],
  }

  const defaultPortalPartner: PortalPartner = {
    invoiceFirstName: '',
    invoiceLastName: '',
    invoiceEmail: '',
    invoiceMinimum: 0,
  }

  const [cbPartnerData, setCBPartnerData] = useState<CBPartnerInfo>(defaultCBPartner)
  const [portalPartnerData, setPortalPartnerData] = useState<PortalPartner>(defaultPortalPartner)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [invoiceEmailError, setInvoiceEmailError] = useState<boolean>(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false)

  const hasUnsavedChanges =
    !hasSubmitted &&
    (!isEqual(cbPartnerData, defaultCBPartner) || !isEqual(portalPartnerData, defaultPortalPartner))

  // Warn on tab close/refresh; in-app navigation is handled by the <Prompt> below
  useEffect(() => {
    if (!hasUnsavedChanges) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const dispatch = useDispatch()

  const { loadingStates, user }: { loadingStates: any; user: any } = useSelector((state: any) => ({
    loadingStates: state.loadingStates,
    user: state.user,
  }))

  const submitData = () => {
    const newCBPartnerData = formatData(cbPartnerData)
    const newPartner = {
      ...pick(newCBPartnerData, [
        'createStagingChain',
        'name',
        'logoImage',
        'communityImpactLink',
        'isPercentBased',
        'isInKind',
        'donationPercentage',
        'donationAmount',
        'useRegionalization',
        'targetChainNonprofitInfo',
        'stores',
      ]),
      ...pick(portalPartnerData, [
        'invoiceFirstName',
        'invoiceLastName',
        'invoiceEmail',
        'invoiceMinimum',
      ]),
      paymentStructureId: portalPartnerData.paymentStructureId
        ? +portalPartnerData.paymentStructureId
        : undefined,
    }
    dispatch(createCentralBackendPartner(newPartner))
  }

  if (['Super', 'Admin'].indexOf(user.type) == -1) return <PageNotFound />

  const isLoadingCBPartner = loadingStates?.cbPartner?.loading
  const apiError = loadingStates?.cbPartner?.error
  return (
    <div>
      <Prompt
        when={hasUnsavedChanges}
        message="You have unsaved changes on this form. Are you sure you want to leave?"
      />
      <h1>Create Partner</h1>
      <EditCBPartnerForm
        formTitle="Central Backend Info:"
        partnerData={cbPartnerData}
        setPartnerData={setCBPartnerData}
        showSubmit={false}
      />
      <EditPortalPartnerForm
        formTitle="Partner Portal Info:"
        partnerData={portalPartnerData}
        setPartnerData={data => {
          if (data.invoiceEmail) setInvoiceEmailError(false)
          setPortalPartnerData(data)
        }}
        invoiceEmailError={invoiceEmailError}
        showSubmit={false}
      />
      <div>
        <BeamButton
          text="SUBMIT"
          handler={() => {
            if (!portalPartnerData.invoiceEmail) {
              setInvoiceEmailError(true)
              return
            }
            setInvoiceEmailError(false)
            setShowModal(true)
            setHasSubmitted(true)
            submitData()
          }}
        />
      </div>
      <BeamModal
        title={isLoadingCBPartner ? 'Processing...' : apiError ? 'Error' : 'Success!'}
        titleColor={isLoadingCBPartner || apiError ? '#000' : '#4caf50'}
        open={showModal}
        handleClose={() => setShowModal(false)}>
        <div>
          {apiError ? (
            processApiError(apiError)
          ) : isLoadingCBPartner ? (
            <BeamProgressBar />
          ) : (
            <span>&#127881;</span>
          )}
        </div>
      </BeamModal>
    </div>
  )
}

export default CreateCentralBackendPartner
